import { enumShort, isoDay, isToday, type StoreRecord } from './chefShared'
import { parseTimestamp } from '../../lib/timestamp'

export const PREP_STATUSES = ['paid', 'accepted', 'preparing', 'ready'] as const
const CONFIRMED_STATUSES = [...PREP_STATUSES, 'delivery_requested', 'in_delivery', 'completed']

export const isConfirmedToday = (order: StoreRecord): boolean =>
  isToday(String(order.scheduled_at || order.created_at || '')) && CONFIRMED_STATUSES.includes(enumShort(order.status))

export const isPrepOrder = (order: StoreRecord): boolean =>
  PREP_STATUSES.includes(enumShort(order.status) as (typeof PREP_STATUSES)[number])

/** Use the same seven local dates for the card, timeline, and dispatch list. */
export const isUpcomingMeal = (order: StoreRecord): boolean => {
  const scheduled = new Date(String(order.scheduled_at ?? ''))
  if (!Number.isFinite(scheduled.getTime())) return false
  const start = new Date()
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 7)
  if (scheduled < start || scheduled >= end) return false
  return isPrepOrder(order) ||
    (enumShort(order.status) === 'pending_payment' && isoDay(scheduled) > isoDay(start))
}

/** Earnings include this calendar week through now, starting on Monday. */
export const isEarnedThisWeek = (timestamp: unknown): boolean => {
  const at = parseTimestamp(timestamp)
  if (at === null) return false
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  start.setDate(start.getDate() - (start.getDay() + 6) % 7)
  return at >= start.getTime() && at <= now.getTime()
}

export const walletOwner = (row: StoreRecord): string =>
  String(row.owner_profile_id ?? row.profile_id ?? row.rider_id ?? '')

export interface PrepDayLoad {
  key: string
  label: string
  weekday: string
  total: number
  firm: number
  held: number
  stages: Record<(typeof PREP_STATUSES)[number], number>
}

/** Unscheduled and overdue active orders belong to today's preparation queue. */
export const prepDayKey = (order: StoreRecord, now = new Date()): string | null => {
  const scheduled = String(order.scheduled_at ?? '')
  if (!scheduled) return isPrepOrder(order) ? isoDay(now) : null
  const when = new Date(scheduled)
  if (!Number.isFinite(when.getTime())) return null
  const key = isoDay(when)
  const today = isoDay(now)
  if (isPrepOrder(order)) return key < today ? today : key
  return enumShort(order.status) === 'pending_payment' && key > today ? key : null
}

/** Timeline cells and their order lists use the same date and status rules. */
export const buildPrepDays = (orders: readonly StoreRecord[], now = new Date()): PrepDayLoad[] => {
  const days = Array.from({ length: 7 }, (_, offset): PrepDayLoad => {
    const day = new Date(now)
    day.setDate(now.getDate() + offset)
    return { key: isoDay(day), label: day.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
      weekday: offset === 0 ? 'Today' : day.toLocaleDateString(undefined, { weekday: 'short' }),
      total: 0, firm: 0, held: 0, stages: { paid: 0, accepted: 0, preparing: 0, ready: 0 } }
  })
  const byKey = new Map(days.map(day => [day.key, day]))
  for (const order of orders) {
    const key = prepDayKey(order, now)
    const day = key ? byKey.get(key) : undefined
    if (!day) continue
    day.total += 1
    if (isPrepOrder(order)) {
      day.firm += 1
      day.stages[enumShort(order.status) as keyof PrepDayLoad['stages']] += 1
    } else day.held += 1
  }
  return days
}
